/**
 * @fileoverview Улучшенная версия преобразования данных нотариуса
 * @description Использует новые сервисы и константы
 */

import { SPECIAL_SERVICE_CODES } from "@/shared/mention/constants";

/**
 * Маппинг полей нотариуса с их отображаемыми названиями
 */
const NOTARY_FIELD_MAPPINGS = [
  {
    templateKey: "iin",
    notaryKey: "identifier",
    displayName: "ИИН нотариуса",
  },
  {
    templateKey: "uuid",
    notaryKey: "uuid",
    displayName: "UUID нотариуса",
  },
  {
    templateKey: "last_name",
    notaryKey: "last_name",
    displayName: "Фамилия нотариуса",
  },
  {
    templateKey: "first_name",
    notaryKey: "first_name",
    displayName: "Имя нотариуса",
  },
  {
    templateKey: "middle_name",
    notaryKey: "middle_name",
    displayName: "Отчество нотариуса",
  },

  {
    templateKey: "previous_fio",
    notaryKey: "previous_full_name",
    displayName: "Предыдущее ФИО нотариуса",
  },
  {
    templateKey: "email",
    notaryKey: "email",
    displayName: "Email нотариуса",
  },
  {
    templateKey: "phone",
    notaryKey: "work_phone",
    displayName: "Телефон нотариуса",
  },
  {
    templateKey: "license_number",
    notaryKey: "license_number",
    displayName: "Номер лицензии нотариуса",
  },
  {
    templateKey: "license_issue_date",
    notaryKey: "license_issue_date",
    displayName: "Дата выдачи лицензии",
  },
  {
    templateKey: "license_status",
    notaryKey: "license_status",
    displayName: "Статус лицензии",
  },
  {
    templateKey: "education_institution",
    notaryKey: "education_institution",
    displayName: "Учебное заведение нотариуса",
  },
  {
    templateKey: "education_specialty",
    notaryKey: "education_specialty",
    displayName: "Специальность нотариуса",
  },
  {
    templateKey: "education_diploma_number",
    notaryKey: "education_diploma_number",
    displayName: "Номер диплома нотариуса",
  },
  {
    templateKey: "registration_date",
    notaryKey: "registration_date",
    displayName: "Дата регистрации нотариуса",
  },
  {
    templateKey: "chamber_join_date",
    notaryKey: "chamber_join_date",
    displayName: "Дата вступления в палату",
  },
  {
    templateKey: "status",
    notaryKey: "status",
    displayName: "Статус нотариуса",
  },
];

/**
 * Преобразует данные нотариуса в формат полей для mention
 * @param {Object} notary - Данные нотариуса
 * @returns {Array<import('@/shared/mention/types').ServiceField>} Массив полей
 */
export function convertNotaryToFields(notary) {
  if (!notary) {
    return [];
  }

  const fields = [];

  // Добавляем составное поле ФИО
  const fio = `${notary.last_name || ""} ${notary.first_name || ""} ${
    notary.middle_name || ""
  }`.trim();

  if (fio) {
    fields.push({
      key: "fio",
      value: fio,
      participant_type_code: null,
      service_code: SPECIAL_SERVICE_CODES.NOTARY,
      display_name: "ФИО нотариуса",
    });
  }

  // Обрабатываем остальные поля
  NOTARY_FIELD_MAPPINGS.forEach((mapping) => {
    const value = notary[mapping.notaryKey];

    // Пропускаем пустые и null значения
    if (value === undefined || value === null || value === "") {
      return;
    }

    const stringValue = String(value).trim();
    if (!stringValue) {
      return;
    }

    fields.push({
      key: mapping.templateKey,
      value: stringValue,
      participant_type_code: null, // Для нотариуса не требуется
      service_code: SPECIAL_SERVICE_CODES.NOTARY,
      display_name: mapping.displayName,
    });
  });

  return fields;
}

/**
 * Получает отображаемое имя для ключа поля нотариуса
 * @param {string} fieldKey - Ключ поля
 * @returns {string} Отображаемое имя
 */
export function getDisplayName(fieldKey) {
  const mapping = NOTARY_FIELD_MAPPINGS.find((m) => m.templateKey === fieldKey);

  return mapping?.displayName || fieldKey;
}

/**
 * Получает все доступные ключи полей нотариуса
 * @returns {string[]} Массив ключей
 */
export function getNotaryFieldKeys() {
  return ["fio", ...NOTARY_FIELD_MAPPINGS.map((m) => m.templateKey)];
}

/**
 * Проверяет, существует ли поле нотариуса с данным ключом
 * @param {string} key - Ключ для проверки
 * @returns {boolean} true, если поле существует
 */
export function isValidNotaryField(key) {
  return getNotaryFieldKeys().includes(key);
}
